<template>
  <div class="py-2">
    <b-overlay :show="isLoading">
      <vams-collapse title="Pre-SO Jobs" :visible="defaultShowPreSOJobs ? defaultShowPreSOJobs : visible">
        <b-table
          :items="displayedPreSOJobs"
          :fields="fields"
          table-variant="blue"
          thead-tr-class="text-small"
          tbody-tr-class="bg-half-alpha"
        >
          <template #head(selected)>
            <div class="text-center">
              <input v-model="selectAll" type="checkbox" />
            </div>
          </template>

          <template #cell(selected)="{ item }">
            <div class="text-center">
              <input
                v-show="!item.isAssigned && !item.isAssignedOther"
                v-model="item.isSelected"
                type="checkbox"
                @click="select(item)"
              />
              <font-awesome-icon
                v-show="item.isAssigned"
                v-b-tooltip.hover
                icon="check"
                class="text-success"
                :title="`Assigned to this SO: ${item.serviceOrderId}, Op#: ${item.operationId}`"
              />
            </div>
          </template>

          <template #cell(jobId)="{ item }">
            <div v-if="item.isAssigned">{{ item.excedeJobId }} - {{ item.excedeJobDescription }}</div>
            <v-select
              v-else
              :value="item.excedeJob"
              label="description"
              placeholder="Select Operation"
              :class="{ 'is-invalid': hasValidationError(item) }"
              :options="nonScheduledOperationsList"
              :clearable="true"
              :filterable="true"
              :filter-by="operationDropdownSearchFilter"
              @input="handleOperationSelection(item, $event)"
            >
              <template #selected-option="operation">
                <span v-if="operation && operation.id" class="text-uppercase">
                  {{ operation.id }} - {{ operation.description }}
                </span>
              </template>
              <template #option="operation">
                <span class="text-uppercase">{{ operation.id }} - {{ operation.description }}</span>
              </template>
            </v-select>
          </template>
        </b-table>
      </vams-collapse>
    </b-overlay>
  </div>
</template>

<script>
import CollapseComponent from '@/shared/components/ui/CollapseComponent';

import { mapGetters, mapMutations } from 'vuex';
import { ServiceOrderGetters } from '@/shared/store/service-order/types';
import { UnitGetters } from '@/shared/store/unit/types';
import { PreSOJobsGetters, PreSOJobsMutations } from '@/shared/store/pre-so-jobs/types';
import { UserGetters } from '@/shared/store/user/types';
import { LookupGetters } from '@/shared/store/lookup/types';
import vSelect from 'vue-select';
import { helpers } from 'vuelidate/lib/validators';

function requiredIfSelected(value, parentVm) {
  return parentVm.isSelected ? helpers.req(value) : true;
}
export default {
  name: 'ServiceOrderAddPreSOJobs',
  components: {
    'vams-collapse': CollapseComponent,
    'v-select': vSelect
  },
  props: {
    visible: Boolean()
  },
  data: function () {
    return {
      fields: [
        { key: 'selected', label: '', sortable: false, tdClass: 'align-middle', thStyle: 'width: 25px' },
        { key: 'jobId', label: 'Job ID', sortable: true, tdClass: 'align-middle' },
        { key: 'jobDescription', label: 'Job Description', sortable: true, tdClass: 'align-middle' },
        { key: 'techRemarks', label: 'Tech Remarks', sortable: true, tdClass: 'align-middle' }
      ],
      isLoading: false,
      defaultShowPreSOJobs: false
    };
  },
  validations() {
    return {
      preSOJobs: {
        $each: {
          excedeJob: {
            requiredIfSelected
          }
        }
      }
    };
  },
  computed: {
    ...mapGetters({
      preSOJobs: PreSOJobsGetters.GET_UNIT_PRE_SO_JOBS,
      unit: UnitGetters.GET_UNIT,
      serviceOrderId: ServiceOrderGetters.GET_SERVICE_ORDER_ID,
      user: UserGetters.GET_USER_PROFILE,
      nonScheduledOperationsList: LookupGetters.GET_NON_SCHEDULED_OPERATIONS_LIST,
      showPreSOJobs: PreSOJobsGetters.GET_SHOW_PRE_SO_JOBS,
      serviceOrder: ServiceOrderGetters.GET_SERVICE_ORDER
    }),
    displayedPreSOJobs() {
      return this.preSOJobs.filter(job => {
        return !job.isAssignedOther && job.branchId === this.serviceOrder.branchId;
      });
    },
    selectAll: {
      get() {
        return (
          this.preSOJobs.length ==
          this.preSOJobs.filter(job => job.isSelected || job.isAssigned || job.isAssignedOther).length
        );
      },
      set(value) {
        this.preSOJobs.forEach(job => {
          if (!job.isAssigned && !job.isAssignedOther) {
            this.setJobProperty(job.id, 'isSelected', value);
          }
        });
      }
    }
  },
  watch: {
    preSOJobs: {
      handler() {
        this.preSOJobs.forEach(job => {
          if (job.serviceOrderId && job.serviceOrderId === this.serviceOrderId) {
            this.setJobProperty(job.id, 'isAssigned', true);
          }
          if (job.serviceOrderId && job.serviceOrderId != this.serviceOrderId) {
            this.setJobProperty(job.id, 'isAssignedOther', true);
          }
        });
      },
      immediate: true
    },
    showPreSOJobs: {
      handler() {
        this.defaultShowPreSOJobs = this.showPreSOJobs;
      },
      immediate: true
    },
    serviceOrder: {
      handler() {
        if (this.preSOJobs.length > 0) {
          let preSOJobBranch = this.preSOJobs.find(job => job.isSelected)?.branchId;
          if (preSOJobBranch && this.serviceOrder.branchId != preSOJobBranch) {
            this.preSOJobs.forEach(job => {
              this.setJobProperty(job.id, 'isSelected', false);
            });
          }
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    ...mapMutations([PreSOJobsMutations.SET_JOB_PROPERTY]),
    isAssigned(job) {
      const jobIds = Object.values(this.selectedJobs).map(job => job.id);
      return jobIds.includes(job.id);
    },
    hasValidationError(item) {
      const index = this.preSOJobs.findIndex(j => j.id === item.id);
      if (index === -1) return false;
      return this.$v.preSOJobs.$each[index].excedeJob.$error;
    },
    validateForSubmission() {
      this.$v.preSOJobs.$touch();
      return !this.$v.preSOJobs.$invalid;
    },
    select(job) {
      var jobToSelect = this.preSOJobs.find(j => j.id === job.id);
      this.setJobProperty(jobToSelect.id, 'isSelected', !jobToSelect.isSelected);
      if (jobToSelect.excedeJob?.id) {
        this.setJobProperty(jobToSelect.id, 'excedeJob', null);
      }
    },
    handleOperationSelection(item, selectedOperation) {
      if (!item.isSelected) {
        this.setJobProperty(item.id, 'isSelected', true);
      }
      this.setJobProperty(item.id, 'excedeJob', selectedOperation);
      this.setJobProperty(item.id, 'excedeJobId', selectedOperation.id);
    },
    setJobProperty(id, property, value) {
      this[PreSOJobsMutations.SET_JOB_PROPERTY]({ id, property, value });
    },

    operationDropdownSearchFilter(option, label, search) {
      return this.dropdownSearchFilter(option.id, option.description, search);
    },
    dropdownSearchFilter(id, description, search) {
      search = search.toLowerCase();
      id = id.toLowerCase();
      description = description.toLowerCase();

      return id.indexOf(search) > -1 || description.indexOf(search) > -1;
    }
  }
};
</script>
